import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon, RotateCwIcon } from 'lucide-react';
import { FLYWHEEL_STAGES } from '../../data/dgpt';
import { SectionHeading } from '../ui/SectionHeading';
import { Reveal } from '../ui/Reveal';
import { cn } from '../../src/lib/utils';
const RADIUS = 41;
export function FlywheelSection() {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => setActive(value => (value + 1) % FLYWHEEL_STAGES.length), 1800);
    return () => window.clearInterval(id);
  }, [reduceMotion]);
  return <section id="dealflow" aria-labelledby="flywheel-heading" className="relative overflow-hidden border-b border-hairline-soft bg-ink" data-id="element-90" data-mp="1vssv4g7j75js">
      <div className="relative mx-auto grid w-full max-w-[1320px] gap-14 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:px-8 lg:py-28" data-id="element-91" data-mp="3tk5dexc8xvxp">
        <div className="flex flex-col gap-6" data-id="element-92" data-mp="2wzvs6a9buysy">
          <SectionHeading eyebrow="The Network Effect" headingId="flywheel-heading" title={<>
                Every Company Makes the <em data-id="element-94" data-mp="2n6f0bbsr3trx">Intelligence Better.</em>
              </>} data-id="element-93" data-mp="2s7vxaw846w38" />
          <Reveal delay={0.06} className="flex flex-col gap-4 text-[15px] leading-relaxed text-text-secondary" data-id="element-95" data-mp="2ib9dmariyb2s">
            <p data-id="element-96" data-mp="3fv40n8lzfv2m">
              StartupFuel has built relationships across more than{' '}
              <span className="font-semibold text-text-primary" data-id="element-97" data-mp="3ayvehb9db1ly">500 startup communities</span> around
              the world.
            </p>
            <p data-id="element-98" data-mp="31066ca79wviw">Those ecosystems generate a growing stream of startup and venture intelligence.</p>
            <p data-id="element-99" data-mp="1ujlw2jg5kcd6">
              DiligenceGPT structures that information so investment teams can move beyond analyzing
              companies already in their inbox and begin discovering opportunities they may never
              have seen.
            </p>
          </Reveal>
        </div>

        {/* Desktop / tablet: circular flywheel */}
        <Reveal delay={0.1} className="hidden sm:block" data-id="element-100" data-mp="llm2uw2pmm59">
          <div className="relative mx-auto aspect-square w-full max-w-[580px]" data-id="element-101" data-mp="7ne5wo5obku3">
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-102" data-mp="7picxeyvczyl">
              <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="#E8E6E1" strokeWidth="0.3" data-id="element-103" data-mp="3u2qjn0ra2n8o" />
              <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(32,22,95,0.3)" strokeWidth="0.35" strokeDasharray="1 3" data-id="element-104" data-mp="2o19pcswx1eu" />
              <circle cx="50" cy="50" r="24" fill="none" stroke="#F0EEEA" strokeWidth="0.3" data-id="element-105" data-mp="3q1n4yhkyux91" />
            </svg>

            {!reduceMotion ? <motion.div aria-hidden="true" className="absolute inset-0" animate={{
            rotate: 360
          }} transition={{
            duration: 14.4,
            ease: 'linear',
            repeat: Infinity
          }} data-id="element-106" data-mp="3g2xv9c384j3v">
                <span className="absolute left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-lime ring-4 ring-lime/15" style={{
              top: `${50 - RADIUS}%`,
              marginTop: '-4px'
            }} data-id="element-107" data-mp="2sgvn2jso9ldt" />
              </motion.div> : null}

            <div className="absolute left-1/2 top-1/2 flex h-[38%] w-[38%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-[#E3DFF4] bg-[#FBFAFF] text-center shadow-[0_20px_50px_-30px_rgba(32,22,95,0.35)]" data-id="element-108" data-mp="3ffi6vvibm9ul">
              <span className="text-lg font-semibold tracking-[-0.02em] text-text-primary lg:text-xl" data-id="element-109" data-mp="3p7xj0c6xs47k">
                DiligenceGPT<span className="text-[0.55em] text-text-tertiary" data-id="element-110" data-mp="2ymmunn1i05r">™</span>
              </span>
              <span className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-lime" data-id="element-111" data-mp="3u4weay2c6h7c">
                <RotateCwIcon className="h-3 w-3" aria-hidden="true" data-id="element-112" data-mp="1risgaa8g1dxd" />
                Compounding
              </span>
            </div>

            <ol aria-label="Intelligence flywheel stages" data-id="element-113" data-mp="1wg0xa7huvbx1">
              {FLYWHEEL_STAGES.map((stage, index) => {
              const angle = index / FLYWHEEL_STAGES.length * Math.PI * 2 - Math.PI / 2;
              const x = 50 + RADIUS * Math.cos(angle);
              const y = 50 + RADIUS * Math.sin(angle);
              const isActive = index === active;
              return <li key={stage} className="absolute w-[30%] -translate-x-1/2 -translate-y-1/2" style={{
                left: `${x}%`,
                top: `${y}%`
              }} data-id="element-114" data-mp="1dj99bkrdc8mn" data-mpi={index}>
                    <div className={cn('rounded-lg border px-2.5 py-2 text-center text-[10px] font-semibold uppercase leading-tight tracking-[0.1em] transition-all duration-500 lg:text-[11px]', isActive ? 'border-[#D8D4EA] bg-[#F3F2FA] text-lime shadow-sm' : 'border-hairline bg-panel text-text-secondary')} data-id="element-115" data-mp="1nhysrhywy150">
                      <span className="mb-0.5 block font-mono text-[9px] text-text-tertiary" data-id="element-116" data-mp="dhct9h4m4xg1">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      {stage}
                    </div>
                  </li>;
            })}
            </ol>
          </div>
        </Reveal>

        {/* Mobile: vertical loop */}
        <Reveal delay={0.1} className="sm:hidden" data-id="element-117" data-mp="3qoeam3m7klyn">
          <ol className="flex flex-col items-stretch gap-1.5" aria-label="Intelligence flywheel stages" data-id="element-118" data-mp="3qraa3wdpfip7">
            {FLYWHEEL_STAGES.map((stage, index) => <li key={stage} className="flex flex-col items-center gap-1.5" data-id="element-119" data-mp="1owhyosw4pa3c" data-mpi={index}>
                <div className={cn('w-full rounded-lg border px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors duration-500', index === active ? 'border-lime/50 bg-lime/10 text-lime' : 'border-hairline bg-panel text-text-secondary')} data-id="element-120" data-mp="1jyggea6j1esx">
                  {stage}
                </div>
                {index < FLYWHEEL_STAGES.length - 1 ? <ArrowDownIcon className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" data-id="element-121" data-mp="11d4z3w1g975" /> : <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-lime" data-id="element-122" data-mp="5yd9vbh5j5wo">
                    <RotateCwIcon className="h-3 w-3" aria-hidden="true" data-id="element-123" data-mp="3n9ml09k2b0kd" />
                    Back to discovery — DiligenceGPT™
                  </span>}
              </li>)}
          </ol>
        </Reveal>
      </div>
    </section>;
}